import Link from 'next/link'
import { useRouter } from 'next/router'
import { PropsWithChildren, useMemo } from 'react'

import {
  ButtonRebrand,
  ButtonRebrandProps
} from 'src/components/atoms/ButtonRebrand'
import { Image } from 'src/components/atoms/Image'
import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'

type CarouselCardRebrandProps = {
  title: string
  eyebrow?: string
  titleHref?: string
  description: string
  backgroundColor:
    | 'lavenderPale'
    | 'limePale'
    | 'linen'
    | 'wineRich'
    | 'forestDeep'
  imageUrl: string
  stat?: React.ReactNode
  isOnlyCard?: boolean
  entireCardClickable?: boolean
}

const Container = ({
  children,
  className,
  entireCardClickable = false,
  titleHref
}: PropsWithChildren<{
  className: string
  entireCardClickable?: boolean
  titleHref?: string
}>) => {
  if (entireCardClickable && titleHref) {
    return (
      <Link href={titleHref} className={className}>
        {children}
      </Link>
    )
  }

  return <div className={className}>{children}</div>
}

const CarouselCardRebrand = ({
  title,
  eyebrow,
  titleHref,
  description,
  backgroundColor,
  imageUrl,
  stat,
  isOnlyCard,
  entireCardClickable
}: CarouselCardRebrandProps) => {
  const router = useRouter()

  const backgroundColorClass = useMemo(() => {
    switch (backgroundColor) {
      case 'lavenderPale':
        return 'bg-lavenderPale text-forestDeep'
      case 'limePale':
        return 'bg-limePale text-forestDeep'
      case 'linen':
        return 'bg-linen text-forestDeep'
      case 'wineRich':
        return 'bg-wineRich text-cream'
      case 'forestDeep':
        return 'bg-forestDeep text-limeFresh'
      default:
        return ''
    }
  }, [backgroundColor])

  const buttonVariant = useMemo<ButtonRebrandProps['variant']>(() => {
    switch (backgroundColor) {
      case 'lavenderPale':
      case 'limePale':
      case 'linen':
        return 'forest'
      case 'wineRich':
      case 'forestDeep':
        return 'lime'
      default:
        return 'forest'
    }
  }, [backgroundColor])

  return (
    <Container
      className={`snap-center md:snap-start ${isOnlyCard ? 'w-full' : ''}`}
      entireCardClickable={entireCardClickable}
      titleHref={titleHref}
    >
      <article
        className={`flex flex-col-reverse md:flex-row gap-10 h-140 md:h-100 ${
          isOnlyCard ? 'w-full' : 'w-[min(720px,calc(100vw-48px))]'
        } max-w-full ${backgroundColorClass} p-4 md:p-8`}
      >
        <div className='flex flex-1 flex-col justify-between min-w-0 h-full'>
          <div className='flex flex-1 flex-col gap-5'>
            {titleHref && !entireCardClickable ? (
              <Link className='hover:underline' href={titleHref}>
                <TypographyRebrand variant='h5'>{title}</TypographyRebrand>
              </Link>
            ) : (
              <TypographyRebrand variant='h5'>{title}</TypographyRebrand>
            )}
            {eyebrow ? (
              <TypographyRebrand variant='eyebrow'>{eyebrow}</TypographyRebrand>
            ) : null}
            <div className='items-start flex flex-1 flex-col justify-between gap-4'>
              <div className={`${stat ? 'line-clamp-5' : 'line-clamp-7'}`}>
                <TypographyRebrand variant='paragraph'>
                  {description}
                </TypographyRebrand>
              </div>
              {titleHref && !entireCardClickable ? (
                <ButtonRebrand
                  variant={buttonVariant}
                  onClick={() => {
                    router.push(titleHref)
                  }}
                >
                  Read more
                </ButtonRebrand>
              ) : null}
            </div>
          </div>
          {stat ? (
            <div className='flex flex-col gap-0 justify-self-end'>{stat}</div>
          ) : null}
        </div>
        <div className='relative md:min-h-70 min-w-0 flex-1 overflow-hidden min-h-0'>
          <Image
            src={imageUrl}
            alt={title}
            fill
            className='object-cover'
            sizes='(max-width: 768px) 78vw, 40rem'
          />
        </div>
      </article>
    </Container>
  )
}

export default CarouselCardRebrand
